import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Switch, FormControlLabel, Typography } from "@material-ui/core";
import PageContainer from "@jumbo/components/PageComponents/layouts/PageContainer";
import FilePicker from "@jumbo/Dashboards/Banner/FilePicker";
import axios from "services/auth/jwt/config";

const fields = [
  ["eyebrow", "متن بالای عنوان"], ["title", "عنوان"], ["subtitle", "ادامه عنوان"], ["highlight", "بخش رنگی عنوان"],
  ["description", "توضیحات"], ["note", "یادداشت زیر دکمه‌ها"], ["imageAlt", "متن جایگزین تصویر"],
  ["bookTitle", "عنوان کارت کتاب"], ["bookText", "متن کارت کتاب"], ["heartTitle", "عنوان کارت قلب"],
  ["heartText", "متن کارت قلب"], ["caption", "زیرنویس تصویر"],
];
const emptySlide = order => ({ image: "", order, published: false, primary: { label: "", to: "" }, secondary: { label: "", to: "" } });
const errorText = error => error.response?.data?.msgFa || error.response?.data?.msg || error.message;
const validUrl = value => /^(\/(?!\/)|#[^\s]*|https?:\/\/)/i.test(value) && !Array.from(value).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127 || char === "\\");

function SlideSection({ title, children }) {
  return <Box component="fieldset" m={0} mb={5} px={3} pt={2} pb={3} border={1} borderColor="divider" borderRadius={8} style={{ minWidth: 0 }}>
    <Typography component="legend" variant="subtitle1" color="primary" style={{ padding: "0 8px", fontWeight: 600 }}>{title}</Typography>
    <Grid container spacing={2}>{children}</Grid>
  </Box>;
}

export default function DesktopSlides() {
  const [slides, setSlides] = useState([]);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(null);
  const load = async () => {
    setLoading(true); setError("");
    try { const { data } = await axios.get("/desktop-slides"); setSlides(data); }
    catch (err) { setError(errorText(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const change = (key, value) => setDraft(current => ({ ...current, [key]: value }));
  const renderFields = keys => keys.map(key => {
    const label = fields.find(field => field[0] === key)[1];
    return <Grid item xs={12} sm={["description", "note"].includes(key) ? 12 : 6} key={key}>
      <TextField id={`desktop-slide-${key}`} fullWidth label={label} value={draft[key] || ""} multiline={key === "description"} minRows={key === "description" ? 3 : undefined} inputProps={{ maxLength: key === "description" ? 3000 : ["note", "bookText", "heartText", "caption"].includes(key) ? 500 : 300 }} onChange={e => change(key, e.target.value)} />
    </Grid>;
  });
  const save = async event => {
    event.preventDefault();
    if (!validUrl(draft.image.trim()) || draft.image.trim().startsWith("#")) { setError("نشانی تصویر باید یک مسیر سایت یا لینک http/https باشد."); return; }
    for (const key of ["primary", "secondary"]) {
      const action = draft[key] || {};
      if (Boolean(action.label?.trim()) !== Boolean(action.to?.trim()) || (action.to?.trim() && !validUrl(action.to.trim()))) {
        setError("برای هر دکمه، متن و مقصد معتبر را با هم وارد کنید یا هر دو را خالی بگذارید."); return;
      }
    }
    setSaving(true); setError("");
    try {
      if (draft._id) await axios.patch(`/desktop-slides/${draft._id}`, draft);
      else await axios.post("/desktop-slides", draft);
      setDraft(null); await load();
    } catch (err) { setError(errorText(err)); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    setSaving(true); setError("");
    try { await axios.delete(`/desktop-slides/${deleting._id}`); setDeleting(null); await load(); }
    catch (err) { setError(errorText(err)); }
    finally { setSaving(false); }
  };
  return <PageContainer heading="اسلایدشو دسکتاپ" breadcrumbs={[]}>
    <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
      <Typography>اسلایدهای بالای صفحه اصلی دسکتاپ؛ ترتیب نمایش از عدد کوچک‌تر به بزرگ‌تر است.</Typography>
      <Button color="primary" variant="contained" disabled={loading} onClick={() => { setError(""); setDraft(emptySlide(Math.max(-1, ...slides.map(slide => slide.order)) + 1)); }}>اسلاید جدید</Button>
    </Box>
    {error && !draft && <Typography color="error" role="alert">{error}</Typography>}
    {loading ? <Typography role="status">در حال دریافت…</Typography> : <>
      <Button onClick={load}>بارگذاری مجدد</Button>
      {!slides.length && <Typography>هنوز اسلایدی ثبت نشده است.</Typography>}
      <Grid container spacing={3}>{slides.map(slide => <Grid item xs={12} md={6} lg={4} key={slide._id}>
        <Card><CardContent>
          <img src={slide.image} alt={slide.imageAlt || ""} style={{ width: "100%", height: 180, objectFit: "contain", background: "#f4f4f4" }} />
          <Typography variant="h6">{[slide.title, slide.subtitle, slide.highlight].filter(Boolean).join(" ") || "اسلاید تصویری"}</Typography>
          <Typography>ترتیب: {slide.order} — {slide.published ? "منتشر شده" : "پیش‌نویس"}</Typography>
          <Button color="primary" onClick={() => { setError(""); setDraft({ ...slide }); }}>ویرایش</Button>
          <Button color="secondary" onClick={() => setDeleting(slide)}>حذف</Button>
        </CardContent></Card>
      </Grid>)}</Grid>
    </>}
    <Dialog open={Boolean(draft)} onClose={() => { if (!saving) setDraft(null); }} fullWidth maxWidth="md"
      scroll="paper" PaperProps={{ component: "form", onSubmit: save }}>
      {draft && <>
        <DialogTitle>{draft._id ? "ویرایش اسلاید" : "اسلاید جدید"}</DialogTitle>
        <DialogContent dividers style={{ minHeight: 0, overflowY: "auto" }}>
          {error && <Typography color="error" role="alert">{error}</Typography>}
          <Typography paragraph>فقط تصویر الزامی است. بخش‌های خالی در سایت نمایش داده نمی‌شوند.</Typography>
          <SlideSection title="نمایش و انتشار">
            <Grid item xs={12} sm={6}><TextField fullWidth required type="number" label="ترتیب نمایش" value={draft.order} inputProps={{ min: 0, step: 1 }} onChange={e => change("order", e.target.value === "" ? "" : Number(e.target.value))} /></Grid>
            <Grid item xs={12} sm={6}><FormControlLabel label="انتشار در سایت" control={<Switch checked={draft.published} onChange={e => change("published", e.target.checked)} />} /></Grid>
          </SlideSection>
          <SlideSection title="تصویر اسلاید">
            <Grid item xs={12}><TextField fullWidth required label="نشانی تصویر" value={draft.image} onChange={e => change("image", e.target.value)} inputProps={{ maxLength: 2000 }} /></Grid>
            <Grid item xs={12}><FilePicker type="Image" setCurrMedia={value => change("image", value)} /></Grid>
            {draft.image && <Grid item xs={12}><img src={draft.image} alt={draft.imageAlt || ""} style={{ maxWidth: "100%", height: 180, objectFit: "contain" }} /></Grid>}
            {renderFields(["imageAlt", "caption"])}
          </SlideSection>
          <SlideSection title="عنوان بزرگ">
            {renderFields(["eyebrow", "title", "subtitle", "highlight"])}
            <Grid item xs={12} sm={6}><TextField fullWidth type="number" id="desktop-slide-title-font-size" label="اندازه عنوان بزرگ (پیکسل)" helperText="از ۱۲ تا ۱۲۰ پیکسل؛ خالی برای اندازه پیش‌فرض. روی تمام بخش‌های عنوان بزرگ اعمال می‌شود." value={draft.titleFontSize == null ? "" : draft.titleFontSize} inputProps={{ min: 12, max: 120, step: 1 }} onChange={e => change("titleFontSize", e.target.value === "" ? null : Number(e.target.value))} /></Grid>
          </SlideSection>
          <SlideSection title="توضیحات اسلاید">
            {renderFields(["description"])}
          </SlideSection>
          <SlideSection title="دکمه‌ها و یادداشت">
            {["primary", "secondary"].map((key, index) => <Grid item xs={12} sm={6} key={key}>
              <Typography variant="subtitle2" gutterBottom>دکمه {index === 0 ? "اصلی" : "دوم"}</Typography>
              <Box mb={2}><TextField id={`desktop-slide-${key}-label`} fullWidth label="متن دکمه" value={draft[key]?.label || ""} inputProps={{ maxLength: 100 }} onChange={e => change(key, { ...draft[key], label: e.target.value })} /></Box>
              <TextField id={`desktop-slide-${key}-to`} fullWidth label="مقصد دکمه" helperText="مسیر سایت مانند /dashboard، بخش صفحه مانند #featured یا لینک https://" value={draft[key]?.to || ""} inputProps={{ maxLength: 2000 }} onChange={e => change(key, { ...draft[key], to: e.target.value })} />
            </Grid>)}
            {renderFields(["note"])}
          </SlideSection>
          <SlideSection title="کارت کتاب">
            {renderFields(["bookTitle", "bookText"])}
          </SlideSection>
          <SlideSection title="کارت قلب">
            {renderFields(["heartTitle", "heartText"])}
          </SlideSection>
        </DialogContent>
        <DialogActions><Button disabled={saving} onClick={() => setDraft(null)}>انصراف</Button><Button disabled={saving} type="submit" color="primary" variant="contained">{saving ? "در حال ذخیره…" : "ذخیره"}</Button></DialogActions>
      </>}
    </Dialog>
    <Dialog open={Boolean(deleting)} onClose={() => { if (!saving) setDeleting(null); }}>
      <DialogTitle>این اسلاید حذف شود؟</DialogTitle>
      {error && <DialogContent><Typography color="error" role="alert">{error}</Typography></DialogContent>}
      <DialogActions><Button disabled={saving} onClick={() => setDeleting(null)}>انصراف</Button><Button disabled={saving} color="secondary" onClick={remove}>حذف اسلاید</Button></DialogActions>
    </Dialog>
  </PageContainer>;
}
